import {Injectable} from '@angular/core';
import {doc, DocumentSnapshot, Firestore, getDoc} from '@angular/fire/firestore';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {toStockDocId} from '../../../shared/shared-utils/firestore/firestore.utils';
import {sItemKeyToInt} from '../../../shared-utilities/models-old/datastructures';
import {
  IStockManagerStockCodeAvailability,
  IStockManagerStockCodeConflict,
  TStockCodeCheckTargets,
} from '../models/stock-manager-stock-code-availability.model';

@Injectable({
  providedIn: 'root',
})
export class StockManagerStockCodeAvailabilityService {

  constructor(private readonly firestore: Firestore) {
  }

  /**
   * Looks the stock code up as a document id in each target store's stock collection and reports
   * every store that already uses it. Used to block manual code entry from creating a duplicate.
   */
  async checkStockCodeAvailability(
    stores: TStockCodeCheckTargets,
    stockCode: string,
  ): Promise<IStockManagerStockCodeAvailability> {
    const trimmedCode = stockCode?.trim() ?? '';
    if (!trimmedCode || stores.length === 0) {
      return {conflicts: [], isAvailable: false, stockCode: trimmedCode};
    }

    const documentId = toStockDocId(trimmedCode);
    const lookups = stores.map(async (store): Promise<IStockManagerStockCodeConflict | null> => {
      const stockPath = createSharedPaths().setStoreId(store.storeId).stock();
      const snapshot: DocumentSnapshot = await getDoc(doc(this.firestore, `${stockPath}/${documentId}`));
      if (!snapshot.exists()) {
        return null;
      }
      return {
        existingStockDescription: this.readDescription(snapshot),
        storeId: store.storeId,
        storeName: store.name ?? store.storeId,
      };
    });

    const results = await Promise.all(lookups);
    const conflicts = results.filter((conflict): conflict is IStockManagerStockCodeConflict => conflict !== null);

    return {
      conflicts,
      isAvailable: conflicts.length === 0,
      stockCode: trimmedCode,
    };
  }

  private readDescription(snapshot: DocumentSnapshot): string {
    const data = snapshot.data() as Record<string, unknown> | undefined;
    const description = data?.[sItemKeyToInt.desc];
    return typeof description === 'string' && description.trim() ? description.trim() : 'No description';
  }

}
